/**
 * ==========================================================================
 *  THE KAMIINA, AND THE PRICE OF GETTING PAST IT
 * ==========================================================================
 *  The stove that heats the palju used to stand *in* the water, which made
 *  Hannu's '"Kimmo" hei, pist pari klapii!' a joke about a man who could not
 *  reach his own fire. It stands on the decking now, on the east side of the
 *  tub, with two pipes over the rim: cold off the bottom, hot back in at the
 *  top. That is how a palju actually circulates, and it is why the firebox is
 *  somewhere a runner can throw a log into at speed.
 *
 *  Which is now the toll. The yard's way out towards the juhlanavetta is a
 *  veräjä, the veräjä is shut, and the pair of klapit Hannu keeps asking for
 *  is what opens it. A pair, not one - he shouts for two, and two is what the
 *  fire has to have before the gate moves.
 *
 *  But they are carried **one at a time**, because that is what an armful of
 *  split birch actually is and because it is what turns the toll into a piece
 *  of the level rather than a button on the way past it. The rack is on the
 *  tub's north rim and the firebox is off its east end, so one log is one lap
 *  of the corner - and two logs is that lap twice, with Hannu in the yard for
 *  the whole of it. Four presses of F, in this order and no other:
 *
 *      klapikasa  ->  F  ->  one log in Kimmo's arm
 *      kamiinan luukku + the log  ->  F  ->  the fire takes, and stays shut
 *      klapikasa  ->  F  ->  the second log
 *      kamiinan luukku + the log  ->  F  ->  the veräjä swings, Hannu goes home
 *
 *  The fire lights on the first one. That is the only thing in the yard that
 *  tells a runner "that one counted, and it was not enough" without a line of
 *  UI - the flames come up in the hatch, the gate does not move, and Hannu
 *  shouts for the pair again.
 *
 *  ------------------------------------------------------------------------
 *   WHAT IS LEFT TO DECLINE
 *  ------------------------------------------------------------------------
 *  The yard's fence used to have a second hole in it at the north end, out
 *  into the navetta's own front yard, and paying the toll was a choice between
 *  two corners and the laps of the tub. It is boarded up: the fence runs into
 *  the kuusikko now (`AIDAN_POHJOISPAA` in `src/config/level.ts`), so inside
 *  the yard the veräjä is the only way east and the fire is the only thing
 *  that opens it.
 *
 *  What can still be declined is the yard itself. The way round the outside of
 *  the treeline finishes without a klapi, without Hannu and without the tub,
 *  and it costs the length of the back field - so the toll is a toll to
 *  whoever came in here, and coming in here is what was chosen.
 *
 *  ------------------------------------------------------------------------
 *   NO THREE.JS
 *  ------------------------------------------------------------------------
 *  Same split as Door: this owns everything the simulation can observe, and
 *  the fire, the flames and the log in Kimmo's arm are meshes World reads
 *  back out. It is stepped inside the fixed timestep and it has no clock of
 *  its own, so a run re-simulates to the same millisecond on the server.
 * ==========================================================================
 */

import { KLAPI } from '../config/gameplay';
import { clamp, type Vec3 } from '../utils/math';
import type { Collider } from './CollisionWorld';
import type { KamiinaSpec } from './Level';
import { isLookingAt, type InteractContext, type Interactable } from './Interactable';

/** What one press of F did, for the mixer and the viewmodel. */
export type KlapiEvent = 'taken' | 'burned';

export class Kamiina {
  /** The pile. Reach it empty-handed and F takes one log off the top. */
  readonly pile: Interactable;
  /** The hatch. Reach it carrying one and F puts it through. */
  readonly hatch: Interactable;

  private carrying = false;
  /** How many are in the pesä. The veräjä waits for `KLAPI.logsNeeded`. */
  private burned = 0;
  /** When the most recent log went in, whichever one it was. Drives the fire. */
  private lastKlapiAtTime: number | null = null;
  /** When the *last required* one went in. Drives the veräjä and Hannu. */
  private fedAtTime: number | null = null;
  private gateProgress = 0;

  constructor(
    private readonly spec: KamiinaSpec,
    private readonly gate: Collider,
  ) {
    this.pile = {
      id: 'klapikasa',
      prompt: 'OTA KLAPI',
      focusPoint: () => spec.pile,
      canInteract: (context) =>
        !this.carrying && this.burned < KLAPI.logsNeeded &&
        context.feet.z >= spec.pile.z &&
        Math.abs(context.feet.x - spec.pile.x) <= spec.pileHalfWidth &&
        this.inReach(context, spec.pile),
      interact: () => {
        this.carrying = true;
      },
      reset: () => undefined,
    };

    this.hatch = {
      id: 'kamiina',
      prompt: 'HEITÄ PESÄÄN',
      focusPoint: () => spec.hatch,
      // No `isFed` test here: an empty hand cannot feed it and the pile stops
      // filling one the moment the count is up, so "carrying" is already the
      // whole of the answer.
      canInteract: (context) => this.carrying && this.inReach(context, spec.hatch),
      interact: (context) => {
        this.carrying = false;
        this.burned++;
        this.lastKlapiAtTime = context.simulationTime;
        if (this.burned >= KLAPI.logsNeeded) this.fedAtTime = context.simulationTime;
      },
      reset: () => undefined,
    };
  }

  /** True while Kimmo is running with a split billet in his off arm. */
  get holdingKlapi(): boolean {
    return this.carrying;
  }

  /** How many are in the pesä, 0..`KLAPI.logsNeeded`. */
  get burnedKlapit(): number {
    return this.burned;
  }

  /** True once the pair is in. This is what opens the gate and ends Hannu. */
  get isFed(): boolean {
    return this.fedAtTime !== null;
  }

  /**
   * Simulation time the *last* log went in, or null while the pesä is cold.
   *
   * Deliberately not `fedAt`: the fire has to light on the first one, because
   * a firebox that stayed dark until the toll was paid in full would be the
   * level refusing to admit that something happened.
   */
  get lastKlapiAt(): number | null {
    return this.lastKlapiAtTime;
  }

  /** Simulation time the pair was complete, or null. Drives the leaf. */
  get fedAt(): number | null {
    return this.fedAtTime;
  }

  /** The veräjä's swing, 0..1. Rendering reads this; nothing else does. */
  get gateOpenProgress(): number {
    return this.gateProgress;
  }

  get gatePost(): [number, number] {
    return this.spec.gatePost;
  }

  reset(): void {
    this.carrying = false;
    this.burned = 0;
    this.lastKlapiAtTime = null;
    this.fedAtTime = null;
    this.gateProgress = 0;
    this.gate.enabled = true;
  }

  /**
   * One fixed step. Owns the veräjä's collider, exactly the way Door owns its
   * own: the swing is a pure function of `simulationTime - fedAt`, so there is
   * nothing here for a re-simulation to disagree with.
   */
  simulate(simulationTime: number): void {
    if (this.fedAtTime === null) {
      this.gateProgress = 0;
      this.gate.enabled = true;
      return;
    }
    this.gateProgress = clamp(
      (simulationTime - this.fedAtTime) / KLAPI.gateOpenDuration,
      0,
      1,
    );
    this.gate.enabled = this.gateProgress < KLAPI.gateClearAt;
  }

  /**
   * Close enough, and not behind you.
   *
   * Deliberately not `INTERACT`'s cone - see the note on `KLAPI`. You meet both
   * of these sideways at 30 km/h with your eyes on the veräjä, and a firebox
   * you had to aim at would be a tax on the mouse rather than a test of the
   * line.
   */
  private inReach(context: InteractContext, point: Vec3): boolean {
    return isLookingAt(context, point, KLAPI.reach, KLAPI.minLookDot);
  }
}
